import { gql } from '@apollo/client'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { GA4ViewItemEvent } from '@jusbrasil-web/shared-ga4-events'
import { RefCallback, useCallback, useEffect, useState } from 'react'

import { UseSendViewItemEvent_OfferLockBodyFragment } from './__generated__/use-send-view-item-event.graphql'

type UseSendViewItemEventProps = {
  offerLockBody: UseSendViewItemEvent_OfferLockBodyFragment
}

const triggeredViewItem: Record<string, boolean> = {}

export function useSendViewItemEvent(props: UseSendViewItemEventProps) {
  const { offerLockBody } = props
  const { sendGA4Event } = useGA4Dispatcher()
  const [observer, setObserver] = useState<IntersectionObserver | null>(null)

  const sendViewItemEvent = useCallback(() => {
    const eventProperties = offerLockBody.cards.reduce<Record<string, number[]>>((obj, card) => {
      const { lockEventProperties } = card

      const { feature, next_plan } = lockEventProperties
      if (!triggeredViewItem[feature] && typeof next_plan === 'number') {
        obj[feature] ??= []
        obj[feature].push(next_plan)
      }

      return obj
    }, {})

    const hasCreditCard = offerLockBody.cards.some((card) => card.creditCard?.ccToken)
    Object.entries(eventProperties).forEach(([feature, nextPlansIds]) => {
      sendGA4Event(
        new GA4ViewItemEvent({
          checkoutMethod: hasCreditCard ? 'one_click' : 'payment_form',
          nextPlansIds: nextPlansIds,
          triggerFeature: feature,
        })
      )
      triggeredViewItem[feature] = true
    })
  }, [offerLockBody, sendGA4Event])

  useEffect(() => {
    if (global.IntersectionObserver) {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            intersectionObserver.disconnect()
            sendViewItemEvent()
          }
        },
        { threshold: 0.1 }
      )
      setObserver(intersectionObserver)

      return () => {
        intersectionObserver.disconnect()
        setObserver(null)
      }
    }
  }, [sendViewItemEvent])

  useEffect(() => {
    return () => {
      Object.keys(triggeredViewItem).forEach((key) => {
        triggeredViewItem[key] = false
      })
    }
  }, [])

  const sendViewItemRef: RefCallback<HTMLElement> = useCallback(
    (element) => {
      if (observer && element) {
        observer.observe(element)
      }
    },
    [observer]
  )

  return { sendViewItemRef }
}

useSendViewItemEvent.fragments = {
  offerLockBody: gql`
    fragment UseSendViewItemEvent_offerLockBody on OfferLockBody {
      cards {
        creditCard {
          ccToken
        }
        lockEventProperties {
          feature
          next_plan
        }
      }
    }
  `,
}
